import { useEffect, type RefObject } from 'react';

import { AudioEngine, ClientLogicRuntime, useLiveSceneStore } from '../../engine';
import type { HollowScriptRuntimeHandles } from '../../engine/scripting/hollowScriptRuntime';
// Type-only: erased at compile time, so this does not pull quickjs-emscripten-core
// or the guest bundle into whatever bundle this file itself ends up in - only
// `defaultLoadRuntimeCtor`'s dynamic `import(...)` below does that, and only
// once actually called.
import type { Tier2ScriptRuntime } from '../../engine/tier2';
import { TIER2_SCRIPTING_ENABLED } from '../config/tier2ScriptingFeature';
import { usePlayScriptConsoleStore } from './playScriptConsoleStore';

/**
 * Tier-2 counterpart to `src/studio/editor/usePreviewScriptRuntime.ts` -
 * same shape (three plain, directly-testable functions plus a thin hook
 * that calls them), same reason (real, security-relevant wiring deserves
 * automated coverage, not only manual verification), wired into
 * `ScreenPlayRuntime` (`screens-play.tsx`) instead of `PreviewRoute`.
 *
 * The one real difference from the tier-1 version: everything here is
 * gated behind `TIER2_SCRIPTING_ENABLED` (`tier2ScriptingFeature.ts`), off
 * by default until origin isolation ships
 * (docs/editor-consolidation/play-origin-isolation.md) - `attachPlayScriptRuntime`
 * checks the flag *before* dynamically importing `../../engine/tier2` at
 * all, so a disabled session never fetches the QuickJS-WASM guest chunk
 * (quickjs-emscripten-core plus the ~190KB pre-bundled interpreter) over
 * the network in the first place, not merely "constructs it but never uses
 * it".
 */

export interface PlayScriptRuntimeRefs {
  readonly scriptLogicRef: RefObject<ClientLogicRuntime | null>;
  readonly audioEngineRef: RefObject<AudioEngine | null>;
}

/** Builds the live `HollowScriptRuntimeHandles` a published play session's
 * tier-2 scripts run against - identical in shape and behaviour to
 * `usePreviewScriptRuntime.ts`'s `buildPreviewScriptHandles` (same
 * `HollowScriptRuntimeHandles` contract, same "read through the refs at
 * call time" reasoning), reading `ScreenPlayRuntime`'s own refs instead of
 * `PreviewRoute`'s. Kept as a separate function rather than shared with the
 * preview version so each call site's refs stay concretely typed, and so
 * tier 1's file never has to import anything tier-2-shaped. */
export function buildPlayScriptHandles(refs: PlayScriptRuntimeRefs): HollowScriptRuntimeHandles {
  const { scriptLogicRef, audioEngineRef } = refs;
  return {
    liveScene: useLiveSceneStore.getState(),
    logic: {
      variable: (name) => scriptLogicRef.current?.variable(name) ?? 0,
      setVariable: (name, value) => {
        scriptLogicRef.current?.setVariable(name, value);
      },
      applyAction: (action) => {
        scriptLogicRef.current?.applyAction(action);
      },
    },
    audio: {
      playOneShot: (url, category) => audioEngineRef.current?.playOneShot(url, category) ?? Promise.resolve(false),
    },
  };
}

export type Tier2ScriptRuntimeCtor = new (handles: HollowScriptRuntimeHandles) => Tier2ScriptRuntime;

export interface AttachPlayScriptRuntimeDeps {
  /** Defaults to the real dynamic import of `../../engine/tier2` - only
   * called at all when `TIER2_SCRIPTING_ENABLED` is true. Overridable so a
   * test can supply a fake constructor without ever touching the real
   * QuickJS-WASM module. */
  readonly loadRuntimeCtor: () => Promise<Tier2ScriptRuntimeCtor>;
  /** Defaults to the real `TIER2_SCRIPTING_ENABLED` flag - overridable so a
   * test can exercise the enabled path without setting a real env var. */
  readonly enabled: boolean;
}

function defaultLoadRuntimeCtor(): Promise<Tier2ScriptRuntimeCtor> {
  return import('../../engine/tier2').then((m) => m.Tier2ScriptRuntime);
}

/**
 * Constructs a `Tier2ScriptRuntime` from `refs` and registers it into
 * `playScriptConsoleStore` once the dynamic import resolves, so
 * `PlayScriptConsole` can reach it - the tier-2 mirror of
 * `usePreviewScriptRuntime.ts`'s `attachPreviewScriptRuntime`, including the
 * same cleanup/cancellation-race handling. When `deps.enabled` is false
 * (the default posture until origin isolation ships), this is a real no-op:
 * no import, no construction, no store write, and the returned cleanup has
 * nothing to undo - `PlayScriptConsole` never renders in that case either
 * (see that component), so there is nothing left half-wired.
 */
export function attachPlayScriptRuntime(
  refs: PlayScriptRuntimeRefs,
  deps: AttachPlayScriptRuntimeDeps = { loadRuntimeCtor: defaultLoadRuntimeCtor, enabled: TIER2_SCRIPTING_ENABLED },
): () => void {
  if (!deps.enabled) return () => {};
  let cancelled = false;
  void deps.loadRuntimeCtor().then((RuntimeCtor) => {
    if (cancelled) return;
    const runtime = new RuntimeCtor(buildPlayScriptHandles(refs));
    usePlayScriptConsoleStore.getState().setRuntime(runtime);
  });
  return () => {
    cancelled = true;
    usePlayScriptConsoleStore.getState().setRuntime(null);
  };
}

/**
 * Syncs the published game's declared capability grant
 * (`GameSetupDoc.scriptCapabilities`, `ScreenPlayRuntime`'s own fetch of the
 * `gamesetup` play doc) into `playScriptConsoleStore`. Identical in
 * behaviour to `usePreviewScriptRuntime.ts`'s `syncPreviewScriptCapabilities` -
 * kept separate only because it targets `playScriptConsoleStore` instead of
 * `scriptConsoleStore`.
 */
export function syncPlayScriptCapabilities(scriptCapabilities: readonly string[] | undefined): () => void {
  usePlayScriptConsoleStore.getState().setAllowedCapabilities(scriptCapabilities);
  return () => usePlayScriptConsoleStore.getState().setAllowedCapabilities(undefined);
}

/**
 * `ScreenPlayRuntime`'s tier-2 scripting wiring - the first scripting hook
 * this route has had at all (`docs/editor-consolidation/tier2-quickjs-sandbox.md`'s
 * "What's left" item 1). Both effect bodies are one-line calls into the
 * tested functions above, same shape as `usePreviewScriptRuntime`.
 */
export function usePlayScriptRuntime(refs: PlayScriptRuntimeRefs, scriptCapabilities: readonly string[] | undefined): void {
  // Deliberately `[]` - see usePreviewScriptRuntime's identical comment on
  // its own tier-1 effect for why re-running this per render (e.g. from a
  // fresh `{ scriptLogicRef, audioEngineRef }` object literal) would be wrong.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => attachPlayScriptRuntime(refs), []);
  useEffect(() => syncPlayScriptCapabilities(scriptCapabilities), [scriptCapabilities]);
}
